// src/components/OnlineNow.jsx
// The Overview's "Online now" pop-up (3.59.1, owner, Super Admins only):
// each client and how many of its people have the portal open and active in
// the last 5 minutes; clicking a client shows its people (name, email, role,
// signed in, last active), ← back to the clients. Backend:
// GET /portal/overview/online (lib/superCards.js).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import Loader from './Loader';
import EmptyState from './EmptyState';
import Emo from './Emo';

const time = iso => (iso ? new Date(iso).toLocaleTimeString('en-US', { timeZone: timeZone(), hour: 'numeric', minute: '2-digit' }) + ' ' + tzLabel(new Date(iso)) : '—');
const ago = iso => {
  const m = Math.floor((Date.now() - Date.parse(iso)) / 60000);
  return m < 1 ? 'just now' : `${m} min ago`;
};

export default function OnlineNow() {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);   // a client's id

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/portal/overview/online`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed');
      setData(j); setError('');
    } catch (e) { setError(e.message); }
  }, [getToken]);
  // Fresh every 30 seconds while it's open
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (error && !data) return <div style={S.error}>{error}</div>;
  if (!data) return <Loader />;
  const client = open && data.clients.find(c => c.id === open);

  if (client) return (
    <div>
      <button onClick={() => setOpen(null)} style={S.back}>← All clients</button>
      <h4 style={S.h4}>{client.name} · {client.count} online</h4>
      <div style={S.wrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}><th style={S.th}>Person</th><th style={S.th}>Role</th><th style={S.th}>Signed in</th><th style={S.th}>Last active</th></tr></thead>
          <tbody>
            {client.people.map((p, i) => (
              <tr key={p.email} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                <td style={S.td}><div style={{ fontWeight: 700 }}>{p.name || p.email}</div>{p.name && <div style={S.small}>{p.email}</div>}</td>
                <td style={S.td}>{p.role}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{time(p.signedInAt)}</td>
                <td style={{ ...S.td, whiteSpace: 'nowrap', color: Date.now() - Date.parse(p.lastActive) < 180000 ? 'var(--t-2e7d32)' : 'var(--t-546e7a)' }}>{ago(p.lastActive)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <p style={S.sub}><strong>{data.total}</strong> {data.total === 1 ? 'person has' : 'people have'} the portal open, active in the last 5 minutes. Click a client to see who.</p>
      {data.clients.length === 0 ? <EmptyState icon={<Emo e="🟢" />} emptyText="Nobody is online right now." /> : (
        <div style={S.wrap}>
          <table style={S.table}>
            <thead><tr style={S.thead}><th style={S.th}>Client</th><th style={{ ...S.th, textAlign: 'right' }}>Online</th></tr></thead>
            <tbody>
              {data.clients.map((c, i) => (
                <tr key={c.id} onClick={() => setOpen(c.id)} style={{ background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)', cursor: 'pointer' }}
                  role="button" tabIndex={0} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(c.id); } }}>
                  <td style={S.td}>{c.name}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}><span style={S.count}>{c.count} ›</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S = {
  sub: { margin: '0 0 12px', color: 'var(--t-546e7a)', fontSize: 13 },
  h4: { margin: '6px 0 10px', color: 'var(--t-00695c)', fontSize: 15 },
  back: { padding: '6px 12px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--b-c8e6c9)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  wrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', border: '1px solid var(--b-e8f5e9)', maxWidth: 760 },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: { padding: '10px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', borderTop: '1px solid var(--c-f0f0f0)' },
  small: { fontSize: 12, color: 'var(--t-78909c)' },
  count: { fontWeight: 800, color: 'var(--t-00695c)', fontSize: 15 },
  error: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, fontSize: 14 },
};
